import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '@/lib/axios';
import { debounce } from 'lodash';

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
    Pagination,
    PaginationContent,
    PaginationEllipsis,
    PaginationItem,
    PaginationLink,
    PaginationNext,
    PaginationPrevious,
} from "@/components/ui/pagination";
import { useToast } from "@/hooks/use-toast";
import { MagnifyingGlassIcon, PlusIcon } from '@radix-ui/react-icons';
import { useIsUserAdmin } from '@/hooks/authHelpers';

// Skeleton row for loading state
const TableRowSkeleton = () => (
    <TableRow>
        <TableCell><div className="h-4 w-3/4 bg-gray-200 rounded animate-pulse" /></TableCell>
        <TableCell><div className="h-4 w-1/2 bg-gray-200 rounded animate-pulse" /></TableCell>
        <TableCell><div className="h-8 w-24 bg-gray-200 rounded animate-pulse" /></TableCell>
    </TableRow>
);

const CompaniesPage = () => {
    const [companies, setCompanies] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [pagination, setPagination] = useState({
        total: 0,
        page: 1,
        limit: 10,
        total_pages: 0
    });

    const navigate = useNavigate();
    const { toast } = useToast();
    const isAdmin = useIsUserAdmin();

    // API call function
    const fetchCompanies = useCallback(async (query, page, limit) => {
        setIsLoading(true);
        try {
            const response = await axios.get('/companies', {
                params: { search: query, page, limit }
            });
            setCompanies(response.data.data);
            setPagination({
                total: response.data.pagination.total,
                page: response.data.pagination.page,
                limit: response.data.pagination.limit,
                total_pages: response.data.pagination.total_pages
            });
        } catch (error) {
            console.error('Error fetching companies:', error);
            toast({
                title: "Error",
                description: "Failed to load companies. Please try again.",
                variant: "destructive",
            });
        } finally {
            setIsLoading(false);
        }
    }, [toast]);

    // Debounced search handler (triggers fetchCompanies after 300ms of no typing)
    const debouncedFetchCompanies = useCallback(debounce(fetchCompanies, 300), []);

    // Handle search input change (debounces the fetch)
    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setPagination(prev => ({ ...prev, page: 1 })); // Reset to page 1 when searching
        debouncedFetchCompanies(e.target.value, 1, pagination.limit);
    };

    // Fetch immediately when pagination changes
    useEffect(() => {
        fetchCompanies(searchTerm, pagination.page, pagination.limit);
    }, [pagination.page, pagination.limit]);

    // Handle pagination changes (immediate request)
    const handlePageChange = (newPage) => {
        setPagination(prev => ({ ...prev, page: newPage }));
    };

    return (
        <div className="min-h-full min-w-full flex flex-col">
            <Card className="w-full max-w-7xl mx-auto">
                <CardHeader>
                    <CardTitle className="text-3xl font-bold text-center">Companies</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="flex items-center justify-between mb-4">
                        <div className="relative flex-grow max-w-full">
                            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
                            <Input
                                type="text"
                                placeholder="Search companies..."
                                value={searchTerm}
                                onChange={handleSearchChange}
                                className="pl-9"
                            />
                        </div>
                        {isAdmin && (
                            <Button onClick={() => navigate('/companies/create')} className="ml-4">
                                <PlusIcon className="w-4 h-4 mr-2" />
                                Create Company
                            </Button>
                        )}
                    </div>

                    <div className="rounded-md border">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-[50%]">Company Name</TableHead>
                                    <TableHead className="w-[30%]">Created At</TableHead>
                                    <TableHead className="w-[20%]">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {isLoading ? (
                                    [...Array(pagination.limit)].map((_, index) => <TableRowSkeleton key={index} />)
                                ) : companies?.length > 0 ? (
                                    companies?.map((company) => (
                                        <TableRow key={company.id} className="hover:bg-gray-50">
                                            <TableCell className="font-medium">{company.name}</TableCell>
                                            <TableCell>{new Date(company.created_at).toLocaleDateString()}</TableCell>
                                            <TableCell>
                                                <Button variant="outline" size="sm" onClick={() => navigate(`/companies/${company.id}`)}>
                                                    View Details
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={3} className="text-center py-8">
                                            <Badge variant="secondary">No companies found</Badge>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>

                    {!isLoading && pagination.total_pages > 1 && (
                        <div className="flex justify-center mt-6">
                            <Pagination currentPage={pagination.page} lastPage={pagination.total_pages}>
                                <PaginationContent>
                                    <PaginationItem>
                                        <PaginationPrevious onClick={() => handlePageChange(pagination.page - 1)} />
                                    </PaginationItem>

                                    {[...Array(pagination.total_pages)].map((_, index) => {
                                        const pageNumber = index + 1;
                                        if (pageNumber === 1 || pageNumber === pagination.total_pages ||
                                            (pageNumber >= pagination.page - 1 && pageNumber <= pagination.page + 1)) {
                                            return (
                                                <PaginationItem key={pageNumber}>
                                                    <PaginationLink onClick={() => handlePageChange(pageNumber)} isActive={pageNumber === pagination.page}>
                                                        {pageNumber}
                                                    </PaginationLink>
                                                </PaginationItem>
                                            );
                                        }
                                        if (pageNumber === 2 || pageNumber === pagination.total_pages - 1) {
                                            return <PaginationItem key={pageNumber}><PaginationEllipsis /></PaginationItem>;
                                        }
                                        return null;
                                    })}

                                    <PaginationItem>
                                        <PaginationNext onClick={() => handlePageChange(pagination.page + 1)} />
                                    </PaginationItem>
                                </PaginationContent>
                            </Pagination>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default CompaniesPage;
